import { useDebounce } from '@pesto/hooks';
import { Button } from '@pesto/ui/components/Button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@pesto/ui/components/Command';
import { LoadingSpinner } from '@pesto/ui/components/LoadingSpinner';
import { Popover, PopoverContent, PopoverTrigger } from '@pesto/ui/components/Popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@pesto/ui/components/Tooltip';
import { cn, formatPhoneNumber } from '@pesto/utils';
import { ChevronsUpDown, ExternalLink, ScanSearch, UserPlus } from 'lucide-react';
import { useMemo, useState } from 'react';

import type { BasicUserFragmentFragment } from '../../__generated__/graphql/api';

import { useSearchUsersQuery } from '../../__generated__/graphql/api';
import { routes } from '../../constants/routes';
import { toUserSearchVariables } from './toUserSearchVariables';

export interface PickedUser {
  id?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
}

const userLabel = (user: PickedUser) =>
  [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || user.id || 'Unnamed';

interface UserPickerProps {
  value?: PickedUser | null;
  onChange: (user: PickedUser) => void;
  onCreateUser?: () => void;
  onInspectUser?: (userId: string) => void;
  disabled?: boolean;
  invalid?: boolean;
}

export const UserPicker = ({ value, onChange, onCreateUser, onInspectUser, disabled, invalid }: UserPickerProps) => {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');

  const debouncedTerm = useDebounce(term, 300);
  const [primary, secondary] = toUserSearchVariables(debouncedTerm);

  const primaryQuery = useSearchUsersQuery({ variables: primary, skip: !primary });
  const secondaryQuery = useSearchUsersQuery({ variables: secondary, skip: !secondary });

  const users = useMemo(() => {
    const found = [
      ...(primaryQuery.data?.listUsers?.users ?? []),
      ...(secondaryQuery.data?.listUsers?.users ?? []),
    ].filter((user): user is NonNullable<typeof user> => Boolean(user));

    return [...new Map(found.map((user) => [user.id, user])).values()];
  }, [primaryQuery.data, secondaryQuery.data]);

  const loading = primaryQuery.loading || secondaryQuery.loading;

  const handleSelect = (user: BasicUserFragmentFragment) => {
    onChange(user);
    setOpen(false);
  };

  return (
    <div className="flex w-full flex-row gap-2">
      <div className="flex-1">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              className={cn('bg-background w-full justify-between', invalid && 'border-destructive')}
            >
              <span className={cn('truncate', !value && 'text-muted-foreground')}>
                {value ? userLabel(value) : 'Search for an existing person'}
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[420px] p-0">
            {}
            <Command shouldFilter={false}>
              <CommandInput placeholder="Name or phone number…" value={term} onValueChange={setTerm} />
              <CommandList>
                {loading ? (
                  <div className="flex h-24 items-center justify-center">
                    <LoadingSpinner />
                  </div>
                ) : (
                  <>
                    <CommandEmpty>
                      {debouncedTerm.trim().length < 2 ? 'Type a name or phone number.' : 'Nobody matched.'}
                    </CommandEmpty>
                    <CommandGroup>
                      {users.map((user) => (
                        <CommandItem
                          key={user.id}
                          value={user.id ?? ''}
                          onSelect={() => handleSelect(user)}
                          className="flex items-center gap-2"
                        >
                          <div className="flex min-w-0 flex-1 flex-col">
                            <span className="truncate">{userLabel(user)}</span>
                            <span className="text-muted-foreground truncate text-xs">
                              {[user.email, user.phone ? formatPhoneNumber(user.phone) : null]
                                .filter(Boolean)
                                .join(' · ')}
                            </span>
                          </div>
                          {onInspectUser && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 shrink-0"
                                  aria-label={`Quick view ${userLabel(user)}`}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    onInspectUser(user.id ?? '');
                                  }}
                                >
                                  <ScanSearch className="h-3.5 w-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Quick view — details without leaving this form</TooltipContent>
                            </Tooltip>
                          )}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <a
                                href={`${routes.users}/${user.id}`}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(event) => event.stopPropagation()}
                                aria-label={`Open ${userLabel(user)} in a new tab`}
                                className="text-muted-foreground hover:text-foreground shrink-0 p-1"
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            </TooltipTrigger>
                            <TooltipContent>Full view — opens their page in a new tab</TooltipContent>
                          </Tooltip>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
      {onCreateUser && (
        <Button
          type="button"
          variant="ghost"
          onClick={onCreateUser}
          disabled={disabled}
          className="text-muted-foreground hover:text-foreground h-10 w-auto shrink-0 font-normal"
          title="Create a person starlink does not know yet"
        >
          <UserPlus className="mr-1 h-4 w-4" /> New person
        </Button>
      )}
    </div>
  );
};
